import { Link } from "react-router-dom";
import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { usePendingReviews, useApproveReview, useRejectReview, useUpdatePendingReview } from "@/hooks/usePendingReviews";
import { PendingReviewCard } from "@/components/admin/PendingReviewCard";
import { FileText, Inbox, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const AdminPendingReview = () => {
  const { data: pendingReviews, isLoading } = usePendingReviews();
  const approveReview = useApproveReview();
  const rejectReview = useRejectReview();
  const updateReview = useUpdatePendingReview();
  const { toast } = useToast();

  const handleApprove = (review: Parameters<typeof approveReview.mutate>[0]) => {
    approveReview.mutate(review, {
      onSuccess: () => {
        toast({
          title: "Published successfully ✓",
          description: `${review.item_type} has been approved and published.`,
        });
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleReject = (id: string) => {
    rejectReview.mutate(id, {
      onSuccess: () => {
        toast({
          title: "Rejected",
          description: "Item has been removed from the queue.",
        });
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleUpdate = (id: string, rawData: Record<string, unknown>) => {
    updateReview.mutate({ id, rawData }, {
      onSuccess: () => {
        toast({
          title: "Saved",
          description: "Changes have been saved.",
        });
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  return (
    <div className="min-h-screen bg-background page-enter">
      <Header />
      
      <main className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-8">
            <BackNav to="/admin" label="Back to Dashboard" />
          </div>

          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="font-display font-medium text-4xl mb-2">Pending Reviews</h1>
              <p className="font-mono text-xs uppercase text-muted-foreground">
                审核队列 — Approve or reject submissions
              </p>
            </div>
            <Link 
              to="/admin/content"
              className="inline-flex items-center gap-2 px-4 py-2 border border-border font-mono text-sm hover:bg-muted transition-colors"
            >
              <Settings className="w-4 h-4" />
              Manage Content
            </Link>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-pulse font-mono text-muted-foreground">Loading...</div>
            </div>
          ) : !pendingReviews || pendingReviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Inbox className="w-16 h-16 text-muted-foreground mb-4" />
              <h2 className="font-display font-medium text-2xl mb-2">Queue Empty</h2>
              <p className="font-mono text-sm text-muted-foreground mb-6">
                No pending items to review
              </p>
              <Link 
                to="/admin/content"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-mono text-sm hover:bg-primary/90"
              >
                <FileText className="w-4 h-4" />
                Manage Published Content
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pendingReviews.map((review) => (
                <PendingReviewCard
                  key={review.id}
                  review={review}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onUpdate={handleUpdate}
                  isApproving={approveReview.isPending}
                  isRejecting={rejectReview.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminPendingReview;
